// src/components/MiniArtworkCard.js
import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { publicGet } from "../lib/apiPublic";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Normalize media URL to relative for CRA proxy
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/media/")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url.replace(/^https?:\/\/[^/]+/, "");
  }
  const trimmed = url.replace(/^\.?\/*/, "");
  return trimmed.startsWith("media/") ? `/${trimmed}` : `/media/${trimmed}`;
}

/**
 * MiniArtworkCard
 * - Auto-sliding image carousel (no extra deps)
 * - Pause on hover, arrows on hover, dot indicators
 * - Works with { id } or { artwork } prop
 */
export default function MiniArtworkCard({
  id,
  artwork: initialArtwork,
  width = 240,
  imgHeight = 140,
  autoMs = 3000,
  onClick,           // optional: click handler for entire card
  onViewArtwork,     // optional: button/click handler to open details
  imagesOnly = false // NEW: when true, show only the image slider (no meta)
}) {
  const [artwork, setArtwork] = React.useState(initialArtwork || null);
  const [loading, setLoading] = React.useState(!initialArtwork && !!id);
  const [hover, setHover] = React.useState(false);
  const [idx, setIdx] = React.useState(0);

  // Fetch artwork by id if needed
  React.useEffect(() => {
    let cancel = false;
    if (!initialArtwork && id) {
      (async () => {
        try {
          const { data } = await publicGet(`/artworks/${id}/`, { params: { view: "card" } });
          if (!cancel) setArtwork(data);
        } catch (e) {
          if (!cancel) setArtwork(null);
          console.warn("MiniArtworkCard fetch failed", e);
        } finally {
          if (!cancel) setLoading(false);
        }
      })();
    } else {
      setLoading(false);
    }
    return () => { cancel = true; };
  }, [id, initialArtwork]);

  // Build image list (from images[] or fallback image/image_url), de-duped
  const imageUrls = React.useMemo(() => {
    if (!artwork) return [];
    const urls = [];
    if (Array.isArray(artwork.images)) {
      for (const im of artwork.images) {
        const src = im?.url || im?.image || im?.image_url;
        if (src) urls.push(toRelativeMedia(src));
      }
    }
    if (urls.length === 0 && (artwork.primary_image_url || artwork.image || artwork.image_url)) {
      urls.push(toRelativeMedia(artwork.primary_image_url || artwork.image || artwork.image_url));
    }
    const seen = new Set();
    return urls.filter((u) => (seen.has(u) ? false : (seen.add(u), true)));
  }, [artwork]);

  // Keep current index in range as images change
  React.useEffect(() => {
    if (!imageUrls.length) { setIdx(0); return; }
    setIdx((prev) => (prev >= imageUrls.length ? 0 : prev));
  }, [imageUrls]);

  // Auto-advance
  React.useEffect(() => {
    if (hover || imageUrls.length <= 1 || imagesOnly) return; // keep mini cards simple in imagesOnly mode
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % imageUrls.length);
    }, autoMs);
    return () => clearInterval(t);
  }, [imageUrls, hover, autoMs, imagesOnly]);

  const goPrev = React.useCallback(() => {
    if (!imageUrls.length) return;
    setIdx((i) => (i - 1 + imageUrls.length) % imageUrls.length);
  }, [imageUrls]);

  const goNext = React.useCallback(() => {
    if (!imageUrls.length) return;
    setIdx((i) => (i + 1) % imageUrls.length);
  }, [imageUrls]);

  if (loading) {
    return (
      <Box sx={{ width, p: 1, border: "1px solid #e5e7eb", borderRadius: 1, bgcolor: "#fff" }}>
        <Typography variant="body2" color="text.secondary">Loading artwork…</Typography>
      </Box>
    );
  }
  if (!artwork) {
    return (
      <Box sx={{ width, p: 1, border: "1px solid #e5e7eb", borderRadius: 1, bgcolor: "#fff" }}>
        <Typography variant="body2" color="error.main">Failed to load artwork.</Typography>
      </Box>
    );
  }

  const title = artwork.title || "Artwork";
  const price = artwork.price ? `$${artwork.price}` : null;
  const artistName =
    (artwork.artist?.user?.first_name || "") +
    (artwork.artist?.user?.last_name ? ` ${artwork.artist.user.last_name}` : "");

  return (
    <Box
      sx={{
        width,
        p: imagesOnly ? 0.5 : 1,
        //border: "1px solid #070707ff",
        borderRadius: 1.5,
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "rgba(95, 90, 90, 0.26)",
        gap: imagesOnly ? 0 : 0.6
      }}
      onClick={onClick}
    >
      {!imagesOnly && (
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            m: 0,
            minHeight: 40,
            lineHeight: 1.25,
            fontWeight: 700,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </Typography>
      )}

      {/* Slider area */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: imgHeight,
          borderRadius: 1.5,
          overflow: "hidden",
          backgroundColor: "rgba(95, 90, 90, 0.26)",
          //backdropFilter: 'blur(14px)',
          userSelect: "none",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {imageUrls.length ? (
          <>
            <img
              key={imageUrls[idx]}
              src={imageUrls[idx]}
              alt={title}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer"
            />

            {imageUrls.length > 1 && (
              <>
                <IconButton
                  aria-label="Previous"
                  onClick={(e) => { e.stopPropagation(); goPrev(); }}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 4,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.9)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    display: hover ? "flex" : "none",
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Next"
                  onClick={(e) => { e.stopPropagation(); goNext(); }}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 4,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.9)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    display: hover ? "flex" : "none",
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </>
            )}

            {/* Dot indicators */}
            {imageUrls.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 6,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 0.75,
                }}
              >
                {imageUrls.map((_, i) => (
                  <Box
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: i === idx ? "common.white" : "rgba(255,255,255,0.6)",
                      border: "1px solid rgba(0,0,0,0.15)",
                      cursor: "pointer",
                    }}
                    title={`Image ${i + 1}`}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ width: "100%", height: "100%", bgcolor: "#f3f4f6" }} />
        )}
      </Box>

      {/* Meta (hidden in imagesOnly mode) */}
      {!imagesOnly && (
        <>
          {price && (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 0.9, mt: 0.5 }}>
              <strong>Price:</strong> {price}
            </Typography>
          )}
          {artistName && (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 0.9 }}>
              <strong>Artist:</strong> {artistName}
            </Typography>
          )}
          {artwork.artwork_type && (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 0.9 }}>
              <strong>Category:</strong> {artwork.artwork_type}
            </Typography>
          )}

          {/* Optional external handler (e.g., open full page) */}
          {onViewArtwork && (
            <Box sx={{ mt: 1 }}>
              <button
                onClick={(e) => { e.stopPropagation(); onViewArtwork(artwork.id); }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                View Artwork
              </button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
